// Texting: one thread per contact. Contacts are NPC accounts (and the island Mayor) for now;
// the thread format is ready for real players once the multiplayer server exists.
// NPCs reply after a delay; replies depend on what you said and how well you know them.

import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { addTrust, playerClout } from "../player";
import { liveTokens } from "../market/market";
import { accountById } from "../social/feed";
import { house, vibe } from "../houses/houses";
import { TUNABLES } from "@degen/shared/config/tunables";

export interface Message {
  from: "me" | "them";
  text: string;
  tick: number;
}

export interface Thread {
  contactId: string; // "acct:N" | "mayor" (later: "player:<id>")
  msgs: Message[];
  relationship: number; // 0-100
  unread: number;
  lastBonusDay: number;
}

export interface PendingReply {
  contactId: string;
  tick: number;
  text: string;
  inviteAccepted?: boolean;
}

export interface MessagesState {
  threads: Record<string, Thread>;
  pending: PendingReply[];
}

export const MAYOR = "mayor";

export function createMessages(): MessagesState {
  return { threads: {}, pending: [] };
}

export function contactName(state: GameState, id: string): { name: string; handle: string; seed: number } {
  if (id === MAYOR) return { name: "Mayor Degen", handle: "mayor", seed: 777 };
  const a = accountById(state, id);
  return a ? { name: a.name, handle: a.handle, seed: a.pfpSeed } : { name: "anon", handle: "anon", seed: 1 };
}

function thread(state: GameState, id: string): Thread {
  return (state.messages.threads[id] ??= { contactId: id, msgs: [], relationship: id === MAYOR ? 60 : 20, unread: 0, lastBonusDay: 0 });
}

/** A message from them lands in your inbox. */
export function receive(state: GameState, contactId: string, text: string) {
  const t = thread(state, contactId);
  t.msgs.push({ from: "them", text, tick: state.tick });
  if (t.msgs.length > 80) t.msgs.splice(0, t.msgs.length - 80);
  t.unread++;
}

export function unreadCount(state: GameState): number {
  let n = 0;
  for (const id in state.messages.threads) n += state.messages.threads[id].unread;
  return n;
}

export function markRead(state: GameState, contactId: string) {
  const t = state.messages.threads[contactId];
  if (t) t.unread = 0;
}

const INVITE = /come over|pull up|party|my (place|house|crib)|visit|link up|hang/i;
const GREET = /^(gm|gn|hi|hey|hello|yo|sup|wagmi)\b/i;
const ALPHA = /alpha|wen|buy|buying|aping|call|what.*(coin|token)|nfa|pump/i;
const MONEY = /send|loan|borrow|seed phrase|wallet address|lend/i;
const GIVE = /giveaway|airdrop|free/i;

export type MsgResult = { ok: true } | { ok: false; error: string };

export function sendMessage(state: GameState, rng: Rng, contactId: string, text: string): MsgResult {
  const clean = text.trim().slice(0, 280);
  if (!clean) return { ok: false, error: "Type something first." };
  if (contactId !== MAYOR && !accountById(state, contactId)) return { ok: false, error: "No such contact." };
  const t = thread(state, contactId);
  t.msgs.push({ from: "me", text: clean, tick: state.tick });
  const day = dayOf(state.tick);
  if (t.lastBonusDay !== day) {
    t.relationship = Math.min(100, t.relationship + 4);
    t.lastBonusDay = day;
  }
  const delay = rng.int(4, 75);
  const reply = composeReply(state, rng, contactId, clean, t);
  state.messages.pending.push({ contactId, tick: state.tick + delay, ...reply });
  return { ok: true };
}

function composeReply(state: GameState, rng: Rng, contactId: string, text: string, t: Thread): { text: string; inviteAccepted?: boolean } {
  const p = state.player;
  if (contactId === MAYOR) {
    if (INVITE.test(text)) return { text: "The Mayor doesn't do house parties. The Mayor DOES approve of you owning property though. 🏛️" };
    return { text: rng.pick(["Welcome to Degen Island. Try not to get rugged. 🏝️", "Remember: the Farm pays the patient. The trenches pay the lucky.", "Reports of a rug? Tell it to the sleuths. 🕵️"]) };
  }
  const acc = accountById(state, contactId)!;
  if (INVITE.test(text)) {
    const home = state.houses.owned.find((h) => h.id === state.houses.homeId);
    if (!home) return { text: rng.pick(["you got a place? 😅 buy a crib first", "where tho, the park bench? 😂"]) };
    const clout = playerClout(p);
    const chance = Math.min(0.92, 0.2 + t.relationship / 150 + (clout / (clout + 20_000)) * 0.35 + Math.min(0.2, vibe(home) / 100));
    if (rng.chance(chance)) return { text: rng.pick(["omw 🏠🔥", "say less, pulling up", "bet. what's the wifi password", "coming thru with snacks 🍕"]), inviteAccepted: true };
    return { text: rng.pick(["can't today ser, chart watching 📉", "next time fr 🙏", "busy farming points, raincheck"]) };
  }
  if (MONEY.test(text)) return { text: rng.pick(["ser this is exactly how people get scammed 😂", "never send anyone your seed phrase. not even me.", "lol no"]) };
  if (GIVE.test(text)) return { text: rng.pick(["drop the link 👀", "I only do giveaways when I'm up. I'm not up.", "if you host one I'm entering"]) };
  if (ALPHA.test(text)) {
    const live = liveTokens(state.market).filter((x) => !x.rugged).sort((a, b) => b.hype - a.hype);
    const tok = live.length ? rng.pick(live.slice(0, 5)) : null;
    if (tok && acc.personality !== "doomer") return { text: rng.pick([`been watching $${tok.ticker} 👀 NFA`, `$${tok.ticker} chart looks spicy rn`, `aped a little $${tok.ticker}. don't tell anyone`]) };
    return { text: "everything is going to zero. stay safe. 🫠" };
  }
  if (GREET.test(text)) return { text: rng.pick(["gm 🫡", "gm fren", "gm, how are the bags", "wagmi"]) };
  const byPersonality: Record<string, string[]> = {
    alpha: ["say less 🤫", "you're early to this convo", "noted. alpha incoming soon"],
    doomer: ["it's over", "nothing matters, but sure", "hm."],
    shillbot: ["🚀🚀🚀", "have you heard of $MOON yet", "LFG"],
    sleuth: ["interesting. I'm taking notes 🕵️", "send receipts", "hm, suspicious"],
    influencer: ["love that for you 💅", "content idea tbh", "ok bestie"],
    builder: ["cool, back to shipping", "we build 🛠️", "nice. what are you working on?"],
    memelord: ["lmaooo", "😂😂", "this is going in the group chat"],
  };
  return { text: rng.pick(byPersonality[acc.personality] ?? ["fr"]) };
}

/** Per tick: deliver replies, run visits, and occasionally get texted first. */
export function messagesTick(state: GameState, rng: Rng) {
  const m = state.messages;
  if (m.pending.length) {
    const due = m.pending.filter((r) => r.tick <= state.tick);
    m.pending = m.pending.filter((r) => r.tick > state.tick);
    for (const r of due) {
      receive(state, r.contactId, r.text);
      if (r.inviteAccepted && state.houses.homeId) {
        const from = state.tick + rng.int(20, 120);
        state.houses.visits.push({ contactId: r.contactId, houseId: state.houses.homeId, fromTick: from, toTick: from + rng.int(180, 360), rewarded: false });
        pushLog(state, "visit_planned", `${contactName(state, r.contactId).name} is coming over to your place soon. 🏠`);
      }
    }
  }

  // Visits end: hosting builds relationships and followers; 3+ at once is a house party.
  for (const v of state.houses.visits) {
    if (v.rewarded || state.tick < v.toTick) continue;
    v.rewarded = true;
    const t = thread(state, v.contactId);
    t.relationship = Math.min(100, t.relationship + 10);
    const h = state.houses.owned.find((x) => x.id === v.houseId);
    const crowd = state.houses.visits.filter((o) => o.houseId === v.houseId && o.fromTick < v.toTick && o.toTick > v.fromTick).length;
    const party = crowd >= 3;
    const acc = accountById(state, v.contactId);
    const gain = Math.round(Math.sqrt(acc?.followers ?? 100) * 0.4 * (1 + (h ? vibe(h) : 0) / 20) * (party ? 2 : 1));
    state.player.followers += gain;
    if (party) addTrust(state.player, 0.5);
    receive(state, v.contactId, party ? "that party was insane 🔥🔥 posting the pics" : rng.pick(["thanks for having me 🙏", "your place is fire", "had a good time fr"]));
    pushLog(state, party ? "house_party" : "visit_done", party ? `🎉 House party at your ${house(v.houseId)?.name}! +${gain} followers from the pics.` : `${contactName(state, v.contactId).name} had a good time at your place. +${gain} followers.`);
  }
  if (state.houses.visits.length > 30) state.houses.visits = state.houses.visits.filter((v) => !v.rewarded || state.tick - v.toTick < 1_440);

  // Now and then someone you know texts first.
  if (state.tick % 60 === 0 && rng.chance(0.08)) {
    const known = Object.values(m.threads).filter((t) => t.contactId !== MAYOR && t.relationship >= 25);
    const pool = known.length && rng.chance(0.6) ? known.map((t) => t.contactId) : state.social.accounts.map((a) => a.id);
    const id = rng.pick(pool);
    const live = liveTokens(state.market);
    const tok = live.length ? rng.pick(live) : null;
    receive(state, id, rng.pick(["gm", "you see the market today? 😵", tok ? `thoughts on $${tok.ticker}?` : "wen bull", "we should link up sometime", "you hosting anything this week?"]));
  }
}

/** First message after arriving on the island. */
export function welcomeMessage(state: GameState) {
  receive(state, MAYOR, `Welcome to Degen Island, @${state.player.handle}! 🏝️ Your phone has everything: Pumpr, Chirp, Farm, Mintr, Pets, Cars, Houses. Tap the map up top to get around. Don't click weird links.`);
  void TUNABLES;
}
